import * as THREE from 'three';
import {
  AR_PANEL,
  CODE_WORLD_CATALOGUE,
  LECTERN,
  layoutButtons,
  lecternPose,
  subtitlePlate,
  voiceButton,
  voiceReserve,
  VOICE_BUTTON,
  type ButtonLayout,
  type Lesson,
  type LecternPose,
  type WorldCatalogue,
} from '@vrlearning/lesson-schema';
import {
  Button,
  LECTERN_PANEL_MIN,
  Panel,
  panelStyles,
  type ButtonKind,
  type PanelContent,
  type PanelStyle,
} from './panel.js';
import type { LessonPlayer, PlayerState } from '../engine/player.js';
import type { Stage } from './stage.js';
import { sideStep } from '../engine/placement.js';
import { applyLecternPose, furnishLectern, slideLectern, voiceCorner } from './lectern.js';
import { replayOffered } from '../engine/step-change.js';
import type { GuideRender } from '../engine/guide.js';
import { unspokenNarration } from '../engine/step-text.js';
import { PLATE_PROUD, SubtitlePlate, type PlateContent } from './subtitle.js';

type ButtonDef = { label: string; onSelect: () => void; kind?: ButtonKind };

/** "Sla over" (ADR-043): above the lectern's top edge, where a veiled lectern leaves it. */
const SKIP = { label: 'Sla over', above: 0.1 } as const;
/** The lectern fades out and in over this long while a model is inside the clear space round the stage (ADR-043). */
const VEIL_SECONDS = 0.3;

/** Metres "Voorlezen" stands in front of the VR panel it sits on. */
const VOICE_PROUD = 0.002;

/**
 * What "Voorlezen" says (ADR-033): the pupil's own mode, off or on
 * (voice.ts), with a drawn speaker, not the 🔊 glyph a Quest font may lack.
 * On, it is lit: "Voorlezen: aan".
 */
const VOICE_FACE = {
  off: { label: 'Voorlezen', icon: 'speaker', kind: 'voice' },
  on: { label: 'Voorlezen: aan', icon: 'speaker', kind: 'voiceOn' },
} as const;

/**
 * Distance ahead and drop below eye level when pinning (docs/04).
 *
 * 1.0 m, not the 0.75 m the prototype used. On a Quest 3 the panel at 0.75 m
 * reads as being in your face, and it is worse for a question step than for
 * the introduction because the answer buttons sit below the panel, so the
 * whole assembly subtends more of the view. Further back also leaves more
 * room between the panel and the table being measured.
 */
const AR_PANEL_DISTANCE = 1.0;
const AR_PANEL_DROP = 0.3;
/** Walk further than this from the panel and it comes with you. */
const AR_PANEL_FOLLOW_DISTANCE = 2.5;
/** A panel dragged to the floor is unreadable, so it cannot go below this. */
const AR_PANEL_MIN_HEIGHT = 0.4;
/**
 * How far the panel steps to the student's right while the skeleton is
 * unlocked: its own width and a little more, so it is out of the line of
 * sight to the skeleton and still a glance away.
 */
const AR_PANEL_ASIDE = 0.6;
/** How much of the way to where it is going the panel slides each frame. */
const AR_PANEL_SLIDE = 0.2;

// Scratch vectors for the per-frame AR panel placement; never retained.
const POSITION = new THREE.Vector3();
const DIRECTION = new THREE.Vector3();
const TARGET = new THREE.Vector3();
const ZERO = new THREE.Vector3();
const HOVER_RAYCASTER = new THREE.Raycaster();
const GRAB_RAYCASTER = new THREE.Raycaster();

/**
 * The lesson's UI surface.
 *
 * VR puts it on a lectern fixed in the rig: 1.9 m ahead, 0.95 m below the eyes
 * (0.65 m high for an adult), tilted up by 0.5 rad. It moves with the tour
 * rather than the head, so students can look around freely while the text
 * stays put, and swings aside along its arc when it would hide what a step is
 * about (docs/04-lesson-format.md; lesson-schema's framing.ts decides where).
 *
 * AR floats the same surface ~0.75 m ahead at chest height and re-centres only
 * when it leaves the field of view, because in passthrough the student walks
 * around the room.
 */
/**
 * Something pressed and lit like the lectern's buttons that is not one of its own: the test drive's
 * panel (ADR-048), the Bekijken panel and the floor's teleport points (ADR-058). Its mesh carries
 * `userData.button` (with `onSelect`); a teleport point's also `userData.floor`.
 */
export type ExtraPressable = { readonly mesh: THREE.Object3D; hovered: boolean };

export class LessonUi {
  readonly group = new THREE.Group();
  readonly #panel: Panel;
  readonly #buttons = new THREE.Group();
  readonly #cursor: THREE.Group;

  #onFinish: (() => void) | undefined;
  #onReplay: (() => void) | undefined;
  #onVoice: (() => void) | undefined;
  /** "Voorlezen", while it shows; not one of the step's own buttons. */
  #voice: Button | undefined;
  /** The corner the panel's text keeps clear for it, on a step with a voice-over. */
  #reserve: PanelContent['reserve'];
  /** The panel's lower part the text keeps clear on a furnish step: its rooms and palette (ADR-037). */
  #bottom: number | undefined;
  #asked = false;
  readonly #isAr: boolean;
  #live: Button[] = [];
  /** Pressed like the lectern's own buttons, hung by someone else (ADR-048, ADR-058). */
  #extra: ExtraPressable[] = [];
  /** Bekijken mode (ADR-058): the lectern's frame without its panel, words or buttons. */
  #bare = false;
  /** Where the AR panel is pinned in the room; undefined until first placed. */
  #pinnedAt: THREE.Vector3 | undefined;
  /** Set while the student is holding the panel. */
  #drag: { distance: number; offset: THREE.Vector3 } | undefined;
  /** Whether the panel has been asked to stand aside (stepAside). */
  #aside = false;
  /** How far aside of where it is pinned it stands; none when not aside. */
  #asideBy: THREE.Vector3 | undefined;
  /** Where the VR lectern is sliding to; undefined once it is there. */
  #lecternTarget: LecternPose | undefined;
  /** The subtitle plate (ADR-035): VR only; AR keeps its text. */
  readonly #plate: SubtitlePlate | undefined;
  /** What this panel runs every frame, taken back off the stage (which outlives it) in `dispose`. */
  readonly #frameHandlers: (() => void)[] = [];
  /** How this step is guided (engine/guide.ts); `text` until told. */
  #guide: GuideRender = 'text';
  /** The state last drawn by `render`, to draw again when the guide changes. */
  #state: PlayerState | undefined;
  /** A hotspot's card is on the panel. */
  #hotspot = false;
  #onHotspot: ((open: boolean) => void) | undefined;
  #onForward: (() => void) | undefined;
  /** "Sla over" while a Spannend part may be skipped (ADR-043), and where it stands. */
  #skip: Button | undefined;
  readonly #skipHolder = new THREE.Group();
  /** "Bronnen" on the last step (ADR-043), when the lesson has credits to show. */
  #onCredits: (() => void) | undefined;
  /** How far the lectern has faded out, 0 to 1, and where it is going. */
  #veil = 0;
  #veilTarget = 0;
  /** The card that stands through redraws (the Spannend warning), if any. */
  #sticky: { title: string; text: string; buttons: ButtonDef[] } | undefined;

  constructor(
    readonly stage: Stage,
    readonly lesson: Lesson,
    readonly player: LessonPlayer,
    /** The lesson's world as a catalogue (a package's features, or the code world's). */
    readonly worlds: WorldCatalogue = CODE_WORLD_CATALOGUE,
  ) {
    this.#isAr = lesson.mode === 'ar';
    const style: PanelStyle = this.#isAr ? panelStyles.ar : panelStyles.lectern;

    // The buttons are laid out in the panel's own plane by lesson-schema's
    // layoutButtons, which framing measures the lectern by: where they are
    // drawn is where the lectern is kept clear of the models.
    if (this.#isAr) {
      this.#panel = new Panel(AR_PANEL.width, AR_PANEL.height, style, {
        depthTest: false,
        renderOrder: 20,
      });
      this.#panel.mesh.position.y = AR_PANEL.panelY;
      stage.scene.add(this.group);
    } else {
      // As tall as its words (11 Oct 2026), its bottom edge -- and so every button under it --
      // where the full panel's is; the lectern is still placed by the full panel (`lecternAssembly`).
      this.#panel = new Panel(LECTERN.width, LECTERN.height, style, {
        fit: { min: LECTERN_PANEL_MIN, bottom: LECTERN.panelY - LECTERN.height / 2 },
      });
      // 0.95 m below the eyes: 0.65 m for an adult, not the 0.85 the
      // prototypes used, where it sat in front of whatever the step was
      // pointing at (docs/04, 22 Sep 2026) -- and following the eyes, because
      // fixed above the floor it stood at a seated child's eye level (28 Sep).
      // Low enough to clear the content, high enough to read with a glance
      // down: the buttons land about 36 degrees below the horizon, and never
      // more than 40 (a question's two rows of answers raise it).
      applyLecternPose(this.group, lecternPose(stage.eyeHeight, 0, 0));
      stage.rig.add(this.group);
      this.#everyFrame(stage, () => {
        if (!this.#lecternTarget) return;
        slideLectern(this.group, this.#lecternTarget);
        if (
          this.group.position.distanceTo(new THREE.Vector3(...this.#lecternTarget.position)) === 0
        ) {
          this.#lecternTarget = undefined;
        }
      });
    }

    this.group.add(this.#panel.mesh, this.#buttons);
    // "Sla over" follows the lectern but is not in its group: the veil leaves it standing.
    if (!this.#isAr) {
      this.#skipHolder.name = 'skip';
      stage.rig.add(this.#skipHolder);
      this.#everyFrame(stage, (dt) => this.updateVeil(dt));
    }

    // In the panel's plane, where lesson-schema's subtitlePlate puts it, just
    // above the navigation row: in the lectern's group rather than on the
    // panel, which is hidden while it shows. Within what the lectern is
    // measured by (the panel and its buttons), so nothing framing measures moves.
    if (!this.#isAr) {
      const place = subtitlePlate();
      this.#plate = new SubtitlePlate();
      this.#plate.mesh.position.set(place.x, place.y, PLATE_PROUD);
      this.#plate.mesh.renderOrder = this.#panel.mesh.renderOrder + 1;
      this.group.add(this.#plate.mesh);
      const plate = this.#plate;
      this.#everyFrame(stage, (dt) => plate.update(dt));
    }

    /**
     * Where the ray lands.
     *
     * The controller draws a line but nothing marked its end, so aiming at an
     * answer was guesswork -- raised after testing on a Quest, where the AR
     * measuring reticle made the absence obvious by comparison. Built to match
     * that reticle deliberately: one thing to learn, not two.
     *
     * A child of the panel group, so it inherits the lectern's tilt and sits
     * flat against it without any per-frame orientation maths.
     */
    this.#cursor = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.018, 0.026, 32),
      new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, transparent: true }),
    );
    const dot = new THREE.Mesh(
      new THREE.CircleGeometry(0.004, 16),
      new THREE.MeshBasicMaterial({ color: 0x1e6fd9, depthTest: false }),
    );
    // Above the panel's own render order, or the lectern draws over it.
    ring.renderOrder = 30;
    dot.renderOrder = 31;
    this.#cursor.add(ring, dot);
    this.#cursor.visible = false;
    this.group.add(this.#cursor);

    if (this.#isAr) {
      this.pinArPanel();
      this.#everyFrame(stage, () => this.updateArPanel());
    }
  }

  /** The panel's mesh: hidden, not moved, while the voice guides with subtitles. */
  get panelMesh(): THREE.Object3D {
    return this.#panel.mesh;
  }

  /** The subtitle plate's mesh, in VR. */
  get subtitleMesh(): THREE.Object3D | undefined {
    return this.#plate?.mesh;
  }

  /** The panel's body is hidden and the plate may speak: `voice-subtitles`, on an explanation or an introduction. */
  get subtitlesShown(): boolean {
    const phase = this.#state?.phase;
    return (
      this.#plate !== undefined &&
      this.#guide === 'voice-subtitles' &&
      !this.#hotspot &&
      (phase === 'panel' || phase === 'intro')
    );
  }

  /** A hotspot's card is open on the panel. */
  get hotspotOpen(): boolean {
    return this.#hotspot;
  }

  /** Told when a hotspot's card opens, and when it closes. */
  onHotspot(handler: (open: boolean) => void): void {
    this.#onHotspot = handler;
  }

  /** Verder (to the next step) goes through this when set: the guide's pacer (main.ts). */
  onForward(handler: () => void): void {
    this.#onForward = handler;
  }

  /**
   * How this step is guided. Switching shows or hides the panel's body at
   * once, and redraws the navigation when "↻ Nog eens" comes or goes.
   */
  setGuide(render: GuideRender): void {
    if (render === this.#guide) return;
    const guided = (value: GuideRender): boolean => value !== 'text';
    const redraw = guided(render) !== guided(this.#guide);
    this.#guide = render;
    const state = this.#state;
    if (redraw && state && !this.#hotspot && (state.phase === 'panel' || state.phase === 'intro')) {
      this.render(state);
    } else {
      this.#showGuide();
    }
  }

  /** What the plate says: a page, or the title dimmed while the voice loads; nothing unless it shows. */
  setSubtitle(content: PlateContent | undefined): void {
    this.#plate?.set(this.subtitlesShown ? content : undefined);
  }

  /** Whether a line fits the plate on two lines (paging, engine/guide.ts). */
  fitsSubtitle(text: string): boolean {
    return this.#plate?.fits(text) ?? true;
  }

  /** The panel's body shown or hidden for the guide; the plate cleared when it cannot show. */
  #showGuide(): void {
    const subtitles = this.subtitlesShown;
    this.#panel.visible = !subtitles && !this.#bare;
    if (!subtitles) this.#plate?.set(undefined);
  }

  #next(): void {
    if (this.#onForward) this.#onForward();
    else this.player.next();
  }

  #hotspotClosed(): void {
    if (!this.#hotspot) return;
    this.#hotspot = false;
    this.#onHotspot?.(false);
  }

  /** Buttons currently on screen, for ray picking and hover. */
  get pickables(): THREE.Object3D[] {
    // A veiled lectern has nothing to press but "Sla over".
    const meshes: THREE.Object3D[] = this.group.visible
      ? this.#live.map((button) => button.mesh)
      : [];
    // "Voorlezen" and "Sla over" last: answers are picked by their index (main.ts).
    if (this.#voice && this.group.visible) meshes.push(this.#voice.mesh);
    if (this.#skip) meshes.push(this.#skip.mesh);
    // Last, so a quiz's answers keep their indexes (a test lesson has no quiz).
    if (this.group.visible) meshes.push(...this.#extra.map((button) => button.mesh));
    return meshes;
  }

  /** Buttons hung on the lectern's group, or on the floor, by someone else, picked and lit like its own. */
  setExtraButtons(buttons: readonly ExtraPressable[]): void {
    this.#extra = [...buttons];
  }

  /**
   * Bekijken mode (ADR-058): the lectern stays where docs/04 places it, as the anchor of what hangs on
   * it, but shows no panel, words or buttons of its own.
   */
  setBare(bare: boolean): void {
    this.#bare = bare;
    this.#panel.visible = !bare;
    this.#buttons.visible = !bare;
    if (bare) this.#clearButtons();
  }

  /** Whether a picked mesh is "Sla over" (ADR-043), never an answer. */
  isSkipButton(object: THREE.Object3D): boolean {
    return this.#skip !== undefined && object === this.#skip.mesh;
  }

  /**
   * "Sla over" (ADR-043): shown while a Spannend part may be skipped, above
   * the lectern's top edge, where the veil leaves it; `undefined` takes it away.
   */
  setSkip(handler: (() => void) | undefined): void {
    if (!handler) {
      if (!this.#skip) return;
      this.#skip.mesh.removeFromParent();
      this.#skip.dispose();
      this.#skip = undefined;
      return;
    }
    this.#skip?.mesh.removeFromParent();
    this.#skip?.dispose();
    const { width, height } = VOICE_BUTTON.vr;
    this.#skip = new Button(SKIP.label, handler, { width, height, kind: 'secondary' });
    this.#skip.mesh.position.set(0, LECTERN.panelY + LECTERN.height / 2 + SKIP.above, 0);
    this.#skipHolder.add(this.#skip.mesh);
    this.#followLectern();
  }

  /** The skip button's holder stands where the lectern does. */
  #followLectern(): void {
    this.#skipHolder.position.copy(this.group.position);
    this.#skipHolder.rotation.copy(this.group.rotation);
  }

  /** The lectern fades out (ADR-043: a model inside the clear space round the stage in a Spannend part) or back in. */
  setVeiled(veiled: boolean): void {
    this.#veilTarget = veiled ? 1 : 0;
  }

  /** How far it has faded out, 0 to 1. */
  get veil(): number {
    return this.#veil;
  }

  /** Once a frame: the veil a step towards where it is going, over 0.3 s; "Sla over" with the lectern. */
  updateVeil(dt: number): void {
    if (this.#skip) this.#followLectern();
    if (this.#veil === this.#veilTarget) return;
    const step = dt / VEIL_SECONDS;
    this.#veil =
      this.#veilTarget > this.#veil
        ? Math.min(this.#veilTarget, this.#veil + step)
        : Math.max(this.#veilTarget, this.#veil - step);
    const seen = 1 - this.#veil;
    this.group.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return;
      for (const material of (Array.isArray(node.material)
        ? node.material
        : [node.material]) as THREE.Material[]) {
        const base = material.userData['veilBase'] as
          { opacity: number; transparent: boolean } | undefined;
        if (seen >= 1) {
          // Whole again: every material exactly as it was.
          if (!base) continue;
          material.opacity = base.opacity;
          material.transparent = base.transparent;
          delete material.userData['veilBase'];
          continue;
        }
        const kept = base ?? { opacity: material.opacity, transparent: material.transparent };
        material.userData['veilBase'] = kept;
        material.transparent = true;
        material.opacity = kept.opacity * seen;
      }
    });
    this.group.visible = seen > 0;
  }

  /** "Bronnen" on the last step (ADR-043); `undefined`: the lesson has nothing to credit. */
  onCredits(handler: (() => void) | undefined): void {
    this.#onCredits = handler;
  }

  /**
   * A card of the engine's own on the lectern -- the Spannend warning, the
   * Bronnen card -- in place of the step's text and buttons until `render`.
   */
  showCard(
    title: string,
    text: string,
    buttons: ButtonDef[] = [],
    options: { sticky?: boolean } = {},
  ): void {
    // A sticky card (the Spannend warning) stands through every redraw until `clearCard`.
    this.#sticky = options.sticky ? { title, text, buttons } : undefined;
    this.#drawCard(title, text, buttons);
  }

  /** The sticky card goes; the step's own panel is drawn again. */
  clearCard(): void {
    if (!this.#sticky) return;
    this.#sticky = undefined;
    if (this.#state) this.render(this.#state);
  }

  #drawCard(title: string, text: string, buttons: ButtonDef[]): void {
    // A bare lectern (Bekijken, ADR-058) shows no card of the engine's either.
    if (this.#bare) return;
    this.#draw({ title, text, style: this.#isAr ? panelStyles.ar : panelStyles.lectern });
    this.#clearButtons();
    if (buttons.length > 0) this.#addButtons(buttons);
    this.#panel.visible = true;
    this.#plate?.set(undefined);
  }

  /** Whether a picked mesh is "Voorlezen" rather than one of the step's buttons. */
  isVoiceButton(object: THREE.Object3D): boolean {
    return this.#voice !== undefined && object === this.#voice.mesh;
  }

  /** Wires up "Voorlezen": the pupil's press, which plays or stops. */
  onVoice(handler: () => void): void {
    this.#onVoice = handler;
  }

  /**
   * Shows "Voorlezen" in the panel's top-right corner (lesson-schema's
   * `voiceButton`, which `lecternAssembly` measures), lit as "Voorlezen: aan"
   * while the pupil has it on, or takes it away: hidden, not disabled (voice.ts, `voiceButtonShown`).
   */
  setVoice(state: 'off' | 'on' | 'hidden'): void {
    if (state === 'hidden') {
      if (!this.#voice) return;
      this.#voice.mesh.removeFromParent();
      this.#voice.dispose();
      this.#voice = undefined;
      return;
    }
    const face = VOICE_FACE[state];
    if (this.#voice) {
      if (this.#voice.label !== face.label) this.#voice.setFace(face);
      return;
    }
    const place = voiceButton(this.#isAr ? 'ar' : 'vr');
    this.#voice = new Button(face.label, () => this.#onVoice?.(), {
      width: place.width,
      height: place.height,
      kind: face.kind,
      icon: face.icon,
    });
    // A child of the panel itself, in the panel's own space (its middle is
    // `panelY` above the lectern's origin), a hair proud of it -- not in the
    // buttons' group each screen rebuilds. Drawn after the panel and without
    // a depth test, as the cursor is: on the Quest it showed only on question
    // steps (Pieter, 29 Sep 2026), so whether it is seen must not hang on the
    // order and the depth in which a headset draws two surfaces 2 mm apart.
    // Its clear corners are dropped (panel.ts, alphaTest), so the panel shows
    // round it.
    const panel = this.#panel.mesh;
    this.#placeVoice();
    this.#voice.mesh.renderOrder = panel.renderOrder + 1;
    this.#voice.mesh.material.depthTest = false;
    panel.add(this.#voice.mesh);
  }

  /**
   * "Voorlezen" in the panel's top-right corner, in the panel's own space: lowered with its top
   * edge when the VR panel is shorter than its full size.
   */
  #placeVoice(): void {
    if (!this.#voice) return;
    const panel = this.#panel.mesh;
    const place = this.#isAr ? voiceButton('ar') : voiceCorner(this.#panel.height);
    this.#voice.mesh.position.set(place.x, place.y - panel.position.y, VOICE_PROUD);
  }

  get dragging(): boolean {
    return this.#drag !== undefined;
  }

  /**
   * Starts a drag if the ray hits the panel itself.
   *
   * Only the panel body grabs; the buttons on it still click, so choosing an
   * answer and moving the panel out of the way never compete for the same
   * press.
   */
  tryGrab(ray: THREE.Ray): boolean {
    if (!this.#isAr) return false;

    GRAB_RAYCASTER.set(ray.origin, ray.direction);
    if (GRAB_RAYCASTER.intersectObjects(this.pickables, false).length > 0) return false;

    const hit = GRAB_RAYCASTER.intersectObject(this.#panel.mesh, false)[0];
    if (!hit) return false;

    // Hold it at the distance it was grabbed at, keeping the offset between
    // the grab point and the panel's origin, so it does not jump on contact.
    this.#drag = {
      distance: hit.distance,
      offset: new THREE.Vector3().subVectors(this.group.position, hit.point),
    };
    return true;
  }

  /** Moves the held panel along the ray. */
  dragTo(ray: THREE.Ray): void {
    if (!this.#drag) return;
    TARGET.copy(ray.origin).addScaledVector(ray.direction, this.#drag.distance);
    TARGET.add(this.#drag.offset);
    // Keep it off the floor: a panel at ankle height is unreadable and easy to
    // lose track of.
    TARGET.y = Math.max(TARGET.y, AR_PANEL_MIN_HEIGHT);
    this.group.position.copy(TARGET);
  }

  /** Releases the panel and pins it where it was let go. */
  endDrag(): void {
    if (!this.#drag) return;
    this.#drag = undefined;
    this.#pinnedAt = (this.#pinnedAt ?? new THREE.Vector3()).copy(this.group.position);
    // Put down by the student: where it stays, aside or not.
    this.#asideBy = undefined;
  }

  /**
   * Opening a hotspot swaps the lectern text and turns the accent green
   * (docs/04-lesson-format.md). Deliberately not a floating panel near the
   * object: a panel between the viewer and the thing being described hides it.
   *
   * With `paging`, the object has several hotspots on this step: the title says
   * which one this is and "Volgend punt" asks for the next (not "Volgende":
   * beside "Verder" the two read as one, Pieter on the Quest 3, 25 Oct 2026).
   */
  showHotspot(
    title: string,
    text: string,
    state: PlayerState,
    paging?: { page: number; pages: number; onNext: () => void },
  ): void {
    const paged = paging !== undefined && paging.pages > 1;
    this.#draw({
      title: paged ? `${title} · ${paging.page + 1} van ${paging.pages}` : title,
      text,
      style: panelStyles.hotspot,
    });
    this.#clearButtons();
    const defs: ButtonDef[] = [
      { label: '◀ Sluiten', onSelect: () => this.render(state), kind: 'secondary' },
    ];
    if (paged) defs.push({ label: 'Volgend punt ▶', onSelect: paging.onNext, kind: 'secondary' });
    if (state.canGoForward) defs.push({ label: 'Verder ▶', onSelect: () => this.#next() });
    this.#addButtons(defs);
    // The card as today, the plate hidden under it (ADR-035, decision 13).
    const opened = !this.#hotspot;
    this.#hotspot = true;
    this.#showGuide();
    if (opened) this.#onHotspot?.(true);
  }

  /**
   * Pins the AR panel where the student is standing and looking now.
   *
   * Called when a step begins, which is the moment they have just tapped a
   * button on it and are certainly facing it.
   */
  pinArPanel(): void {
    const camera = this.stage.activeCamera;
    camera.getWorldPosition(POSITION);
    camera.getWorldDirection(DIRECTION);
    DIRECTION.y = 0;
    DIRECTION.normalize();

    TARGET.copy(POSITION).addScaledVector(DIRECTION, AR_PANEL_DISTANCE);
    TARGET.y = POSITION.y - AR_PANEL_DROP;

    this.#pinnedAt = (this.#pinnedAt ?? new THREE.Vector3()).copy(TARGET);
    this.#asideBy = this.#aside ? this.#asideOffset() : undefined;
    this.group.position.copy(TARGET).add(this.#asideBy ?? ZERO);
  }

  /**
   * Steps the AR panel to the student's right, or back to where it was pinned.
   *
   * For while the skeleton is unlocked. Pinned a metre in front of the student
   * the panel stands between them and the skeleton, and a skeleton shrunk to
   * fit a desk has its handles behind it; Pieter could not reach them. Only
   * where the lesson put it: a panel the student dragged somewhere stays there.
   */
  stepAside(aside: boolean): void {
    if (!this.#isAr || aside === this.#aside) return;
    this.#aside = aside;
    this.#asideBy = aside ? this.#asideOffset() : undefined;
  }

  /**
   * Puts the VR lectern where framing placed it (engine/step-placement.ts):
   * at its height for these eyes and this step's buttons, centred or swung
   * along its arc, or pulled closer, and turned to face the pupil. Instantly
   * under a fade; otherwise it slides there, so the pupil sees where it went.
   */
  placeLectern(pose: LecternPose, options: { instant?: boolean } = {}): void {
    if (this.#isAr) return;
    if (options.instant) {
      this.#lecternTarget = undefined;
      applyLecternPose(this.group, pose);
      return;
    }
    this.#lecternTarget = pose;
  }

  /** Where the VR lectern stands in the rig this frame, sliding or not; undefined in AR. */
  currentLectern(): LecternPose | undefined {
    if (this.#isAr) return undefined;
    const { position, rotation } = this.group;
    return { position: [position.x, position.y, position.z], yaw: rotation.y, tilt: rotation.x };
  }

  #asideOffset(): THREE.Vector3 | undefined {
    if (!this.#pinnedAt) return undefined;
    const eye = this.stage.activeCamera.getWorldPosition(new THREE.Vector3());
    const at = this.#pinnedAt;
    const by = sideStep([eye.x, eye.y, eye.z], [at.x, at.y, at.z], AR_PANEL_ASIDE);
    return new THREE.Vector3(...by);
  }

  /**
   * Keeps the panel where it was pinned, turning only to face the student.
   *
   * It used to re-centre the instant it left the field of view. In passthrough
   * that reads as the panel chasing you, and it sits between you and the table
   * you are being asked to measure. A panel fixed in the room is something you
   * can look away from and glance back at, which is the whole point of doing
   * this in AR.
   *
   * The one case it does move is walking off to measure something else: past
   * AR_PANEL_FOLLOW_DISTANCE it comes along rather than being left behind in
   * another corner of the classroom.
   */
  updateArPanel(): void {
    const camera = this.stage.activeCamera;

    // While it is being held, the student decides where it goes.
    if (this.#drag) {
      camera.getWorldPosition(POSITION);
      this.group.lookAt(POSITION.x, this.group.position.y, POSITION.z);
      return;
    }

    if (!this.#pinnedAt) {
      this.pinArPanel();
      return;
    }

    camera.getWorldPosition(POSITION);

    if (POSITION.distanceTo(this.#pinnedAt) > AR_PANEL_FOLLOW_DISTANCE) {
      this.pinArPanel();
      return;
    }

    // Where it is pinned, or aside of it; sliding there rather than jumping,
    // so a student sees where it went.
    TARGET.copy(this.#pinnedAt).add(this.#asideBy ?? ZERO);
    this.group.position.lerp(TARGET, AR_PANEL_SLIDE);

    // Yaw only: the panel stays put but never shows you its edge.
    this.group.lookAt(POSITION.x, this.group.position.y, POSITION.z);
  }

  #clearButtons(): void {
    for (const button of this.#live) {
      this.#buttons.remove(button.mesh);
      button.dispose();
    }
    this.#live = [];
  }

  /** The panel, its corner kept for "Voorlezen" on a step that has a voice-over. */
  #draw(content: PanelContent): void {
    this.#panel.draw({
      ...content,
      ...(this.#reserve ? { reserve: this.#reserve } : {}),
      ...(this.#bottom === undefined ? {} : { bottom: this.#bottom }),
    });
    // The VR panel is as tall as these words: "Voorlezen" keeps to its corner.
    this.#placeVoice();
  }

  /**
   * Puts buttons on the panel where lesson-schema's `layoutButtons` says: a
   * navigation row side by side, answers in two columns, each two lines tall
   * (the text wraps and shrinks to fit, panel.ts). Stacked one under another,
   * four long answers ran into the floor and out of the view.
   */
  #addButtons(defs: ButtonDef[], layout: ButtonLayout = 'row'): void {
    const places = layoutButtons(defs.length, layout, this.#isAr ? 'ar' : 'vr');
    defs.forEach((def, index) => {
      const place = places[index]!;
      const button = new Button(def.label, def.onSelect, {
        width: place.width,
        height: place.height,
        ...(def.kind ? { kind: def.kind } : {}),
      });
      button.mesh.position.set(place.x, place.y, 0);
      this.#buttons.add(button.mesh);
      this.#live.push(button);
    });
  }

  /** Rebuilds the surface for the current player state. */
  render(state: PlayerState): void {
    if (this.#bare) {
      this.#state = state;
      return;
    }
    const { step, phase } = state;
    this.#state = state;
    if (this.#sticky) {
      const { title, text, buttons } = this.#sticky;
      this.#drawCard(title, text, buttons);
      return;
    }
    this.#hotspotClosed();
    this.#clearButtons();
    // Kept whether the button shows or not, so the teacher's switch never reflows the text.
    this.#reserve =
      step.voiceover === undefined ? undefined : voiceReserve(this.#isAr ? 'ar' : 'vr');
    this.#bottom =
      step.task?.type === 'furnish' && !this.#isAr ? furnishLectern().textBottom : undefined;

    // A predict step's introduction leads on to its question, which is part
    // of the step: offered even in a class the teacher steers, and never the
    // way out of a lesson.
    const introduction = phase === 'intro';
    const navigation = (): void => {
      const defs: ButtonDef[] = [];
      if (state.canGoBack) {
        defs.push({ label: '◀ Terug', onSelect: () => this.player.previous(), kind: 'secondary' });
      }
      // Seeing a change twice is how "see it change" sticks. The pupil's
      // own, even in a class the teacher steers: it changes no step.
      // And on every step the voice guides, where it plays the voice again too (ADR-035).
      const replay = this.#onReplay;
      const guided = this.#guide !== 'text' && step.voiceover !== undefined;
      if (replay && (replayOffered(this.lesson, state.index, this.worlds) || guided)) {
        defs.push({ label: '↻ Nog eens', onSelect: replay, kind: 'secondary' });
      }
      if (introduction) {
        defs.push({ label: 'Verder ▶', onSelect: () => this.player.beginPredict() });
      } else if (state.canGoForward) {
        defs.push({ label: 'Verder ▶', onSelect: () => this.#next() });
      }

      // Only on the last step, and regardless of who is steering: asking to
      // come out is a student's to do even in a class the teacher is leading.
      const lastStep = !introduction && state.index === this.lesson.steps.length - 1;
      // Who made what the lesson shows (ADR-043): on the last step, beside the way out.
      const credits = this.#onCredits;
      if (lastStep && credits) {
        defs.push({ label: 'Bronnen', onSelect: credits, kind: 'secondary' });
      }
      if (lastStep && this.#onFinish) {
        defs.push(
          this.#asked
            ? { label: 'Wachten op de leerkracht…', onSelect: () => {}, kind: 'secondary' }
            : {
                label: 'Klaar ✓',
                onSelect: () => {
                  this.#asked = true;
                  this.#onFinish?.();
                  // Redrawn so the button admits the ask has been made rather
                  // than sitting there inviting a second press.
                  this.render(this.player.state);
                },
              },
        );
      }

      if (defs.length) this.#addButtons(defs);
    };

    switch (phase) {
      case 'quiz': {
        const quiz = step.quiz!;
        this.#draw({
          title: step.title,
          text: quiz.question,
          style: this.#isAr ? panelStyles.ar : panelStyles.lectern,
        });
        this.#addButtons(
          quiz.options.map((option, index) => ({
            label: option,
            kind: 'muted' as const,
            onSelect: () => this.player.answerQuiz(index),
          })),
          'answers',
        );
        break;
      }

      case 'predict': {
        const predict = step.predict!;
        this.#draw({
          title: step.title,
          text: predict.question,
          style: this.#isAr ? panelStyles.ar : panelStyles.lectern,
        });
        this.#addButtons(
          predict.options.map((option, index) => ({
            label: option,
            kind: 'muted' as const,
            onSelect: () => this.player.answerPredict(index),
          })),
          'answers',
        );
        break;
      }

      case 'task': {
        // What to do depends on the task. This used to say "aim at the first
        // point and pull the trigger" for every task, which is measuring
        // instructions on a step about bones.
        const how =
          step.task?.type === 'assemble'
            ? 'Knijp bij een bot om het op te pakken en laat het los op de lichte plek.'
            : 'Richt op het eerste punt en druk op de trekker.';
        this.#draw({
          title: step.title,
          text: `${step.task?.instruction ?? ''} ${how}`,
          style: this.#isAr ? panelStyles.ar : panelStyles.lectern,
        });
        break;
      }

      case 'result':
      case 'panel':
      case 'intro':
      default: {
        if (phase === 'result' && step.predict && !step.task && state.predicted !== undefined) {
          // The choice is made and the reveal is the next step (docs/10:
          // predict, then see it). Saying what was chosen keeps the guess in
          // mind on the way there; the introduction has been read already.
          this.#draw({
            title: step.title,
            // Guided by the voice, the reveal comes by itself (ADR-035, decision 3).
            text: chosen(
              step.predict.options[state.predicted] ?? '',
              state.canGoForward && this.#guide === 'text',
            ),
            style: this.#isAr ? panelStyles.ar : panelStyles.lectern,
          });
        } else if (step.panel) {
          this.#draw({
            title: step.panel.title,
            text: step.panel.text,
            style: this.#isAr ? panelStyles.ar : panelStyles.lectern,
          });
        } else if (step.task?.type === 'furnish') {
          // A furnish step without a panel says what to do (ADR-037).
          this.#draw({ title: step.title, text: step.task.instruction });
        } else {
          // No panel: its narration, where nobody speaks it (engine/step-text.ts).
          const narration = unspokenNarration(step, this.#guide);
          this.#draw(
            narration === undefined
              ? { title: step.title, text: '' }
              : {
                  title: step.title,
                  text: narration,
                  style: this.#isAr ? panelStyles.ar : panelStyles.lectern,
                },
          );
        }
        navigation();
        break;
      }
    }
    this.#showGuide();
  }

  /** Called after a quiz answer, to show what was right and why. Returns the words drawn (for their reading time). */
  showQuizOutcome(
    correct: boolean,
    correctLabel: string,
    explain: string,
    state: PlayerState,
  ): string {
    const title = correct ? 'Goed!' : 'Net niet';
    const text = (correct ? '' : `Het juiste antwoord is ${correctLabel}. `) + explain;
    this.#draw({ title, text, style: correct ? panelStyles.correct : panelStyles.wrong });
    this.#clearButtons();
    const defs: ButtonDef[] = [];
    if (state.canGoBack) {
      defs.push({ label: '◀ Terug', onSelect: () => this.player.previous(), kind: 'secondary' });
    }
    if (state.canGoForward) defs.push({ label: 'Verder ▶', onSelect: () => this.#next() });
    if (defs.length) this.#addButtons(defs);
    return `${title} ${text}`;
  }

  /**
   * How far a student has got with an assembly, redrawn as each part goes in.
   *
   * A count rather than a bar: "nog 3" is something a child can check against
   * the pile on the tray, and it is what they will say out loud.
   */
  showAssemblyProgress(state: PlayerState, placed: number, total: number): void {
    if (state.phase !== 'task') return;
    const left = total - placed;
    this.#draw({
      title: state.step.title,
      text:
        placed === 0
          ? `${state.step.task?.instruction ?? ''} Knijp bij een bot om het op te pakken en laat het los op de lichte plek.`
          : `${placed} van ${total} op hun plek. Nog ${left} te gaan.`,
      style: this.#isAr ? panelStyles.ar : panelStyles.lectern,
    });
  }

  /**
   * The thing is built. Says so, and names the part that gave the most
   * trouble -- the one worth a second look -- without making it a mark.
   */
  showAssemblyOutcome(hardestLabel: string | undefined, state: PlayerState): string {
    const text =
      'Alle botten zitten op hun plek.' +
      (hardestLabel ? ` Het lastigst was: ${hardestLabel.toLowerCase()}.` : ' Zonder één misser!');
    this.#draw({ title: 'Compleet!', text, style: panelStyles.correct });
    this.#clearButtons();
    const defs: ButtonDef[] = [];
    if (state.canGoBack) {
      defs.push({ label: '◀ Terug', onSelect: () => this.player.previous(), kind: 'secondary' });
    }
    if (state.canGoForward) defs.push({ label: 'Verder ▶', onSelect: () => this.#next() });
    if (defs.length) this.#addButtons(defs);
    return `Compleet! ${text}`;
  }

  showMeasureOutcome(
    text: string,
    correct: boolean,
    state: PlayerState,
    onRetry: () => void,
  ): string {
    const title = correct ? 'Goed geschat!' : 'Bijna!';
    this.#draw({ title, text, style: correct ? panelStyles.correct : panelStyles.wrong });
    this.#clearButtons();
    const defs: ButtonDef[] = [{ label: 'Opnieuw meten', onSelect: onRetry, kind: 'secondary' }];
    if (state.canGoForward) defs.push({ label: 'Verder ▶', onSelect: () => this.#next() });
    this.#addButtons(defs);
    return `${title} ${text}`;
  }

  /**
   * Updates the hover state and the cursor, and reports how far along each ray
   * it landed so the caller can stop the ray there (`Stage.setPointerReach`).
   */
  /**
   * Wires up the way out of a finished lesson.
   *
   * Offered only on the last step: a button to leave that is reachable from
   * the middle of a lesson is a button children will press in the middle of a
   * lesson. Asking is all it does -- the teacher lets them out.
   */
  onFinish(handler: () => void): void {
    this.#onFinish = handler;
  }

  /** Wires up "↻ Nog eens", offered on a VR step that changes something. */
  onReplay(handler: () => void): void {
    this.#onReplay = handler;
  }

  /** Called when the ask has gone, so the button can say so. */
  markAsked(): void {
    this.#asked = true;
  }

  updateHover(rays: THREE.Ray[]): (number | undefined)[] {
    // The panel as well as the buttons, so the cursor glides across the
    // lectern instead of blinking out between the answers.
    const targets =
      this.#panel.visible && this.group.visible
        ? [...this.pickables, this.#panel.mesh]
        : this.pickables;

    const hovered = new Set<unknown>();
    let landed: THREE.Vector3 | undefined;
    const reach: (number | undefined)[] = [];

    for (const [index, ray] of rays.entries()) {
      HOVER_RAYCASTER.set(ray.origin, ray.direction);
      const hit = HOVER_RAYCASTER.intersectObjects(targets, false)[0];
      reach[index] = hit?.distance;
      if (!hit) continue;

      // The cursor is the lectern's: a teleport point on the floor is lit, not marked with it.
      if (hit.object.userData['floor'] !== true) landed ??= hit.point;
      const button: unknown = hit.object.userData['button'];
      if (button) hovered.add(button);
    }

    for (const button of this.#live) button.hovered = hovered.has(button);
    for (const button of this.#extra) button.hovered = hovered.has(button);

    this.#cursor.visible = landed !== undefined;
    if (landed) {
      // Into the panel group's own space, and a hair proud of the surface so
      // it does not fight the panel for the same depth.
      this.group.worldToLocal(this.#cursor.position.copy(landed));
      this.#cursor.position.z += 0.004;
    }

    return reach;
  }

  #everyFrame(stage: Stage, handler: (dt: number) => void): void {
    this.#frameHandlers.push(stage.onFrame(handler));
  }

  dispose(): void {
    for (const stop of this.#frameHandlers.splice(0)) stop();
    this.#clearButtons();
    this.setVoice('hidden');
    this.setSkip(undefined);
    this.#skipHolder.removeFromParent();
    this.#panel.dispose();
    this.#plate?.dispose();
    // The stage outlives a lesson now, so anything added to it has to take
    // itself back off. Freeing the textures is not enough: the group would
    // stay in the scene graph and the next lesson would render through it.
    this.group.removeFromParent();
  }
}

/**
 * "Jij koos: …" after a prediction without a task. In Dutch, as every string
 * the engine draws is (its buttons say "Verder" in an English lesson too).
 * Points at Verder only when it is there: in a class the teacher steers, the
 * teacher moves everyone on to the reveal.
 */
function chosen(option: string, canGoForward: boolean): string {
  const said = /[.!?…]$/.test(option) ? option : `${option}.`;
  return `Jij koos: ${said} ${canGoForward ? 'Druk op Verder om het te zien.' : 'Zo meteen zie je het.'}`;
}
